import useClientsManual from '@gr/portal/hooks/useClientsManual';
import { CampaignTypeEnum, ProviderSwapValidationCodeEnum } from '@gr/shared/enums';
import {
  Auth0Role,
  FilterDataTypeEnum,
  FilterOperatorEnum,
  IClientEntity,
  IClientView,
  ICreateClientRequest,
  IHttpResponse,
  IProvidersChangeForTypeRequest,
  IProvidersChangeForTypeValidationResponse,
  ISearchPagination,
  ISearchRequest,
  ISetDefaultClickerGroupRequest,
  IUpdateClientRequest,
} from '@gr/shared/models';
import { SearchIcon } from '@heroicons/react/outline';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { axiosPost } from '../../authAxios';
import { NotificationType, useNotifications } from '../../contexts/NotificationContext';
import useRoles from '../../hooks/useRoles';
import { Button } from '../shared/Buttons/Button';
import { CircularButton } from '../shared/Buttons/CircularButton';
import { ButtonVariantEnum } from '../shared/Buttons/types';
import { Card } from '../shared/Card';
import { TextInput } from '../shared/Form/TextInput';
import { Pagination } from '../shared/Table/Pagination';
import { Table } from '../shared/Table/Table';
import ClientDetailsPanel from './ClientDetailsPanel';
import { ClientProviderMapping, DEFAULT_PRIORITY_LEVEL, IClientForm } from './ClientForm';
import { ClientPriorityIndicator } from './ClientPriorityIndicator';
import ProviderSwapWarningModal, { IProviderSwapWarning } from '../shared/ProviderSwapWarningModal';
import { getColumns, noAllClientsClientTableOptions } from './types';

const Clients = () => {
  const [clientLoading, setClientLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedItem, setSelectedItem] = useState<IClientView>();
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [tableOptions, setTableOptions] = useState(noAllClientsClientTableOptions);
  const { addNotification } = useNotifications();
  const allowProviderUpdated = useRoles([Auth0Role.GR_ADMIN]);
  // Same GR_ADMIN gate as allowProviderUpdated, kept as a distinct flag so the two features aren't
  // coupled to one variable (MES-2528).
  const allowPriority = useRoles([Auth0Role.GR_ADMIN]);
  const allowDefaultClickerGroup = useRoles([Auth0Role.GR_STAFF_USER]);
  const allowClientEdit = useRoles([Auth0Role.GR_STAFF_USER]);

  const [{ data, loading, error }, refetch] = useClientsManual(tableOptions, false);
  const queryClient = useQueryClient();

  // Provider-swap warning gate (MES-2587): the modal is driven by swapWarnings, and the pending
  // save awaits the promise whose resolver is parked in this ref until the user decides.
  const [swapWarnings, setSwapWarnings] = useState<IProviderSwapWarning[]>([]);
  const providerSwapConfirmRef = useRef<((confirmed: boolean) => void) | null>(null);

  // Swaps already committed in this panel session. Phase 3 fires swaps with Promise.allSettled and
  // the panel stays open on failure, so after a partial failure (sibling swap, clicker-group error)
  // the retry must diff against these — not the pre-save fetch — or it would re-POST committed
  // swaps, re-pausing LIVE campaigns and restarting the client-wide quiet window.
  const appliedProviderSwapsRef = useRef<ClientProviderMapping>({});

  const confirmProviderSwapWarnings = (warnings: IProviderSwapWarning[]): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      providerSwapConfirmRef.current = resolve;
      setSwapWarnings(warnings);
    });

  const handleSwapWarningConfirm = () => {
    // Keep the modal open (buttons disabled) while the commit runs; updateClient closes it when done.
    providerSwapConfirmRef.current?.(true);
    providerSwapConfirmRef.current = null;
  };

  const handleSwapWarningCancel = () => {
    if (!providerSwapConfirmRef.current) {
      return;
    }
    providerSwapConfirmRef.current(false);
    providerSwapConfirmRef.current = null;
    setSwapWarnings([]);
  };
  // Create client hook
  // Update client hook

  const clients = data?.data.records;
  const totalCount = data?.data.totalCount;

  const columns = getColumns(
    {
      name: (item: IClientView) => {
        openDetailsPanel(item);
      },
    },
    {
      // Render the client name followed by the admin-gated priority pill (MES-2528). The Table
      // <td> wrapper still fires onColumnClick, so clicking the name opens the details panel.
      name: (item?: IClientView) => (
        <>
          {item?.name}
          {allowPriority && <ClientPriorityIndicator priorityLevel={item?.priorityLevel} />}
        </>
      ),
    },
    allowProviderUpdated
  );

  const handleSearchOptionChange = (searchOptions: ISearchRequest) => {
    setTableOptions(searchOptions);
  };

  const handleRefetch = async () => {
    try {
      await refetch();
    } catch (err) { }
  };

  useEffect(() => {
    const searchText = tableOptions.filters.find((filter) => filter.fieldName === 'name')?.value;

    handleRefetch();

    setSearchText(searchText ?? '');
  }, [tableOptions]);

  const handleSearch = () => {
    let searchFilter = {
      dataType: FilterDataTypeEnum.STRING,
      fieldName: 'name',
      operator: FilterOperatorEnum.CONTAINS,
      value: searchText,
    };

    const newSearchOptions = { ...tableOptions };

    newSearchOptions.filters = newSearchOptions.filters.filter((filter) => filter.fieldName !== 'name');

    newSearchOptions.filters.push(searchFilter);

    newSearchOptions.pagination = noAllClientsClientTableOptions.pagination;

    setTableOptions(newSearchOptions);
  };

  const handleClear = () => {
    const newSearchOptions = { ...tableOptions };

    newSearchOptions.filters = newSearchOptions.filters.filter((filter) => filter.fieldName !== 'name');

    setTableOptions(newSearchOptions);
  };

  const openDetailsPanel = async (item?: IClientView) => {
    appliedProviderSwapsRef.current = {};
    setSelectedItem(item);
    setShowDetailsPanel(true);
  };

  const closeDetailsPanel = () => {
    setShowDetailsPanel(false);
    setSelectedItem(undefined);
  };

  const createClient = async (formData: IClientForm): Promise<void> => {
    const request: ICreateClientRequest = {
      name: formData.name,
      externalId: formData.externalId,
      isActive: formData.isActive!.value,
      senderIdentifier: formData.senderIdentifier ?? '',
      tcrBrandId: formData.tcrBrandId ?? '',
      messageFlow: formData.messageFlow ?? '',
      twoWayEnabled: formData.twoWayEnabled ?? true,
      priorityLevel: formData.priorityLevel ?? DEFAULT_PRIORITY_LEVEL,
    };

    const response = await axiosPost<IHttpResponse<IClientEntity>>('/clients-create', request);

    if (response?.status === 200) {
      const client = response?.data?.data ?? undefined;
      const clickerGroupId = formData?.defaultClickerGroupId ?? undefined;
      await setDefaultClickerGroup(client, clickerGroupId);
    }
  };

  // Returns false when the user cancelled the provider-swap warning — nothing was persisted.
  const updateClient = async (formData: IClientForm, originalProviders?: ClientProviderMapping): Promise<boolean> => {
    // Dirty check (MES-2587): only swap providers the user actually changed. An untouched or
    // re-selected-identical provider produces no POST, so it can't raise a spurious warning or
    // needlessly re-point campaigns (which pauses LIVE ones for ~2 minutes). Swaps that already
    // committed on an earlier attempt this session take precedence over the pre-save fetch.
    const changedProviderEntries = (formData.providers ?? []).filter(
      (entry) =>
        !!entry?.provider &&
        entry.provider !== (appliedProviderSwapsRef.current[entry.campaignType ?? ''] ?? originalProviders?.[entry.campaignType ?? ''])
    );
    const swapRequests: IProvidersChangeForTypeRequest[] = changedProviderEntries.map((entry) => ({
      clientId: selectedItem?.id!,
      providerId: entry.provider!,
      campaignType: CampaignTypeEnum[entry.campaignType ?? 'FUNDRAISING'],
    }));

    // Phase 1 (pre-flight, zero writes): validate every changed swap before anything is persisted,
    // so a Cancel on the warning below leaves nothing half-applied — client fields included.
    const warnings: IProviderSwapWarning[] = [];
    if (swapRequests.length > 0) {
      const validations = await Promise.allSettled(
        swapRequests.map((swapRequest) =>
          axiosPost<IHttpResponse<IProvidersChangeForTypeValidationResponse>>('/providers-change-for-type', {
            ...swapRequest,
            validateOnly: true,
          })
        )
      );

      for (let i = 0; i < validations.length; i++) {
        const result = validations[i];
        if (result.status === 'rejected') {
          throw result.reason;
        }

        const validation = result.value?.data?.data;
        if (!validation || validation.isValid) {
          continue;
        }

        if (validation.code === ProviderSwapValidationCodeEnum.NO_AREA_CODE_COVERAGE) {
          warnings.push({
            campaignType: swapRequests[i].campaignType,
            blockingAreaCodes: validation.blockingAreaCodes ?? [],
          });
        } else {
          // Provider-not-found (or any future block reason) is never confirmable. Throw an
          // axios-shaped error so saveClient's existing catch surfaces the message as-is.
          const blockedError: any = new Error('The selected provider could not be found.');
          blockedError.response = { status: 400, data: { message: blockedError.message } };
          throw blockedError;
        }
      }
    }

    // Phase 2 (confirm gate): one modal for all flagged providers. Cancel aborts the entire save —
    // nothing has been written yet, and the panel stays open with the edits intact.
    if (warnings.length > 0) {
      const confirmed = await confirmProviderSwapWarnings(warnings);
      if (!confirmed) {
        return false;
      }
    }

    // Phase 3 (commit): client fields first (as before), then the swaps — with the override sent
    // only for the campaign types the user just confirmed, so the guard still protects the rest
    // against drift between pre-flight and commit.
    try {
      const request: IUpdateClientRequest = {
        id: selectedItem?.id!,
        name: formData.name,
        // providerId: formData.providerId ?? '',
        senderIdentifier: formData.senderIdentifier ?? '',
        tcrBrandId: formData.tcrBrandId ?? '',
        messageFlow: formData.messageFlow ?? '',
        externalId: formData.externalId,
        isActive: formData.isActive!.value,
        twoWayEnabled: formData.twoWayEnabled ?? false,
        priorityLevel: formData.priorityLevel,
      };

      await axiosPost<IHttpResponse<IClientEntity>>('/clients-update', request);

      if (swapRequests.length > 0) {
        const warnedCampaignTypes = new Set(warnings.map((warning) => warning.campaignType));
        const results = await Promise.allSettled(
          swapRequests.map((swapRequest) =>
            axiosPost<IHttpResponse<IProvidersChangeForTypeRequest>>(
              '/providers-change-for-type',
              warnedCampaignTypes.has(swapRequest.campaignType)
                ? { ...swapRequest, overrideAreaCodeWarning: true }
                : swapRequest
            )
          )
        );
        // Advance the dirty-check baseline for swaps that committed, so a retry after a partial
        // failure doesn't re-POST them. A rejected swap is deliberately NOT recorded — its retry
        // must re-run the whole handler (including the providerUpdatedAt stamp).
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            appliedProviderSwapsRef.current[changedProviderEntries[index].campaignType ?? ''] = changedProviderEntries[index].provider!;
          }
        });

        // Any provider swap rejection (e.g. a 5xx, a network failure, or a conflict that appeared
        // between pre-flight and commit) must abort the success path. Re-throw the first rejection
        // reason so saveClient's catch resolves it into a user-facing message.
        const failed = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
        if (failed) {
          throw failed.reason;
        }
      }

      const clickerGroupId = formData?.defaultClickerGroupId ?? undefined;
      await setDefaultClickerGroup(selectedItem, clickerGroupId);
      return true;
    } finally {
      // Close the warning modal whether the commit succeeded or threw (no-op when it never opened).
      setSwapWarnings([]);
    }
  };

  const saveClient = async (formData: IClientForm, originalProviders?: ClientProviderMapping): Promise<void> => {
    setClientLoading(true);
    try {
      if (selectedItem) {
        const saved = await updateClient(formData, originalProviders);
        if (!saved) {
          // User cancelled the provider-swap warning: nothing was persisted, the panel stays open
          // with the edits intact, and no success notification fires.
          return;
        }
      } else {
        await createClient(formData);
      }
    } catch (err: any) {
      console.error(err);
      // TODO: Add an error banner on panels
      // Only surface a backend-provided message for client errors (status < 500); otherwise fall
      // back to a generic string so 5xx/network failures don't leak raw Axios text (e.g. the
      // area-code guard's 400 message is shown, but "Request failed with status code 500" is not).
      const content =
        err?.response?.data?.message && err?.response?.status < 500
          ? err.response.data.message
          : 'An unexpected error occurred when attempting to save the Client.';
      addNotification({
        header: 'Error',
        content,
        type: NotificationType.FAILURE,
      });
      return;
    } finally {
      setClientLoading(false);
    }

    closeDetailsPanel();

    handleRefetch();

    // Invalidate cache
    queryClient.invalidateQueries({ queryKey: ['clients'] });

    addNotification({
      header: 'Client saved successfully!',
    });
  };

  const handlePagination = (newPaginationConfig: ISearchPagination) => {
    const newSearchOptions = { ...tableOptions };
    newSearchOptions.pagination = newPaginationConfig;

    handleSearchOptionChange(newSearchOptions);
  };

  const setDefaultClickerGroup = async (client?: IClientEntity | IClientView, clickerGroupId?: string) => {
    const defaultClickerGroupId = (clickerGroupId?.length ?? 0) > 0 ? clickerGroupId : null;

    if (allowDefaultClickerGroup && client?.id) {
      const request: ISetDefaultClickerGroupRequest = {
        clientId: client.id,
        defaultClickerGroupId,
      };

      await axiosPost<IHttpResponse<void>>('/clicker-groups-set-default', request);
    }
  };

  return (
    <>
      <h2 id="clients-title" className="flex justify-between pb-2 dark:text-white">
        Clients
        <div className="flex sm:hidden">
          <CircularButton onClick={() => openDetailsPanel()} />
        </div>
      </h2>
      <div className="flex pb-2">
        <div className="flex">
          <TextInput
            id="nameLookup"
            name="nameLookup"
            placeholder="Name"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            onClear={handleClear}
            onEnter={handleSearch}
            leadingIcon={<SearchIcon />}
            clearable
          />
          <Button
            id="client-search"
            variant={ButtonVariantEnum.SECONDARY}
            className="self-end ml-2"
            onClick={() => {
              handleSearch();
            }}
          >
            Search
          </Button>
        </div>

        <div className="hidden ml-auto sm:flex">
          <Button
            id="add-client"
            variant={ButtonVariantEnum.SECONDARY}
            disabled={!allowClientEdit}
            onClick={() => {
              openDetailsPanel();
            }}
          >
            Add Client
          </Button>
        </div>
      </div>

      <div className="hidden md:flex">
        <Table
          shimmer
          loading={loading}
          columns={columns}
          items={clients}
          count={totalCount}
          tableSearchOptions={tableOptions}
          onSearchOptionChange={handleSearchOptionChange}
          booleanLabels={[{ fieldName: 'isActive', displayValues: { trueLabel: 'Enabled', falseLabel: 'Disabled' } }]}
          sort
          paginate
        />
      </div>

      <ul role="list" className="flex flex-col mt-4 space-y-4 md:hidden">
        {clients?.map((client) => (
          <Card
            key={client.id}
            header={
              <span className="inline-flex items-center">
                {client.name}
                {allowPriority && <ClientPriorityIndicator priorityLevel={client.priorityLevel} />}
              </span>
            }
            action={() => openDetailsPanel(client)}
            actionOnHeaderClick
            padded={false}
          >
            <div className="flex-1 px-3 py-3 space-y-4 truncate">
              <div className="flex flex-row justify-between mt-1 text-sm text-gray-500">
                <div className="flex flex-col">
                  <label className="font-semibold">Status: </label>
                  {allowProviderUpdated && <label className="font-semibold">Provider Updated: </label>}
                  <label className="font-semibold">Created Date: </label>
                  <label className="font-semibold">Active Carrier Exclusion: </label>
                </div>
                <div className="flex flex-col items-end">
                  <span>{client.isActive ? 'Enabled' : 'Disabled'}</span>
                  <span>{format(new Date(`${client.providerUpdatedAt}`), 'MM/dd h:mm a')}</span>
                  <span>{format(new Date(`${client.createdAt}`), 'MM/dd h:mm a')}</span>
                  <span>{client.hasExclusion ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {/* Pagination */}
        <Pagination
          count={totalCount || 0}
          loading={loading}
          paginationConfig={tableOptions.pagination}
          onPaginate={handlePagination}
        />
      </ul>

      <ClientDetailsPanel
        show={showDetailsPanel}
        loading={clientLoading}
        onClosePanel={closeDetailsPanel}
        selectedItem={selectedItem}
        handleSubmit={saveClient}
        isReadOnly={!allowClientEdit}
        sticky={swapWarnings.length > 0}
      />

      <ProviderSwapWarningModal
        show={swapWarnings.length > 0}
        warnings={swapWarnings}
        onConfirm={handleSwapWarningConfirm}
        onCancel={handleSwapWarningCancel}
      />
    </>
  );
};

export default Clients;
